import { useState } from "react";
import type { Content, StepDoc } from "@/app/queries";
import { checkedLabel, Chip, DashedButton, GHOST_INPUT, IconButton, INPUT, isStale, KIND_LOOK, KINDS, todayISO, type Patch } from "./shared";
import { ImportCard } from "./ImportCard";

type Resource = Content["resources"][number];
type TextField = "reading_guide" | "translation_url" | "estimate" | "checked_on";

export function ResourcesTab({
  content,
  patch,
  stepId,
  flush,
  adopt,
}: {
  content: Content;
  patch: (p: Patch) => void;
  stepId: string;
  flush: () => Promise<void>;
  adopt: (doc: StepDoc) => void;
}) {
  const [draft, setDraft] = useState("");
  const [kind, setKind] = useState<(typeof KINDS)[number]>("видео");
  const add = () => {
    const title = draft.trim();
    if (!title) return;
    const isUrl = /^https?:\/\//i.test(title);
    const res: Resource = { id: "", title, meta: `${kind} · добавлен сейчас`, kind, url: isUrl ? title : undefined, units: [] };
    patch((c) => ({ ...c, resources: [...c.resources, res] }));
    setDraft("");
  };
  return (
    <div className="flex flex-col gap-3">
      {content.resources.map((r, i) => (
        <ResourceCard
          key={r.id || `new-${i}`}
          res={r}
          defaultOpen={i === 0}
          update={(fn) => patch((c) => ({ ...c, resources: c.resources.map((x, j) => (j === i ? fn(x) : x)) }))}
          remove={() => patch((c) => ({ ...c, resources: c.resources.filter((_, j) => j !== i) }))}
        />
      ))}
      <form
        className="flex items-center gap-2.5 rounded-md border border-dashed border-bd2 p-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <input aria-label="ссылка или название ресурса" placeholder="ссылка или название ресурса" value={draft} onChange={(e) => setDraft(e.target.value)} className={`${INPUT} flex-1 font-mono text-[12.5px]`} />
        <div className="flex gap-[3px] rounded-sm border border-bd p-[3px]" role="radiogroup" aria-label="тип ресурса">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              onClick={() => setKind(k)}
              className={`cursor-pointer rounded-[3px] border-0 px-2.5 py-[5px] font-mono text-[11px] ${kind === k ? "bg-accsoft text-acc" : "bg-transparent text-mut hover:text-ink"}`}
            >
              {k}
            </button>
          ))}
        </div>
        <button type="submit" className="cursor-pointer rounded-sm border border-acc bg-acc px-3.5 py-[9px] font-mono text-[12px] text-accink">
          добавить
        </button>
      </form>
      <ImportCard stepId={stepId} flush={flush} adopt={adopt} />
    </div>
  );
}

/** Чип «первоисточник» — те же цвета, что у Callout kind=info на S4. */
const PRIMARY_LOOK = { bg: "var(--infosoft)", fg: "var(--info)" };

/** Пустое поле не пишется в документ пустой строкой — ключ убирается. */
function setField(r: Resource, key: TextField, value: string): Resource {
  const next = { ...r };
  if (value.trim() === "") delete next[key];
  else next[key] = value;
  return next;
}

function toggleSkip(u: Resource["units"][number]): Resource["units"][number] {
  if (u.skip) {
    const { skip: _, ...rest } = u;
    return rest;
  }
  return { ...u, skip: true };
}

export function ResourceCard({ res, defaultOpen, update, remove }: { res: Resource; defaultOpen: boolean; update: (fn: (r: Resource) => Resource) => void; remove: () => void }) {
  const [open, setOpen] = useState(defaultOpen);
  const n = res.units.length;
  const skipped = res.units.filter((u) => u.skip).length;
  const look = KIND_LOOK[res.kind] ?? KIND_LOOK.статья!;
  const stale = isStale(res.checked_on);
  const meta = [res.meta, res.estimate, checkedLabel(res.checked_on)].filter(Boolean).join(" · ");
  // Первоисточник вне гейта: «без гайда» у него — не проблема, а норма.
  const guide = n ? { text: `гайд ${n} юн.${skipped ? ` · ${skipped} ⏭️` : ""}`, fg: "var(--ok)" } : res.optional ? { text: "вне гейта", fg: "var(--info)" } : { text: "без гайда", fg: "var(--warn)" };
  const setUnit = (i: number, fn: (u: Resource["units"][number]) => Resource["units"][number]) => update((r) => ({ ...r, units: r.units.map((x, j) => (j === i ? fn(x) : x)) }));
  return (
    <article className="overflow-hidden rounded-md border border-bd bg-card" style={res.optional ? { borderLeft: "2px solid var(--info)" } : undefined} data-testid="resource-card">
      <div className="grid grid-cols-[22px_minmax(0,1fr)_112px_84px_26px_26px] items-center gap-3 px-3.5 py-3">
        <span className="cursor-grab font-mono text-[12px] text-mut" aria-hidden>
          ⠿
        </span>
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold leading-[1.35]">{res.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] text-mut">
            <span>{meta}</span>
            {stale && (
              <span className="rounded-sm px-1.5 py-px text-warn" style={{ background: "var(--warnsoft)" }}>
                проверить: старше 6 месяцев
              </span>
            )}
          </div>
        </div>
        <span className="flex flex-col gap-1">
          <Chip look={look}>{res.kind}</Chip>
          {res.optional && <Chip look={PRIMARY_LOOK}>первоисточник</Chip>}
        </span>
        <span className="font-mono text-[11px]" style={{ color: guide.fg }}>
          {guide.text}
        </span>
        <IconButton onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={open ? "свернуть юниты" : "развернуть юниты"} className="text-[13px]">
          {open ? "−" : "+"}
        </IconButton>
        <IconButton onClick={remove} aria-label={`удалить ресурс ${res.title}`}>
          ×
        </IconButton>
      </div>
      {open && (
        <>
          <ResourceFields res={res} update={update} stale={stale} />
          <div className="border-t border-bd bg-bg2">
            <div className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1.2fr)_34px_26px] gap-3 border-b border-bd px-3.5 py-2 font-mono text-[10px] uppercase tracking-[.05em] text-mut">
              <span>#</span>
              <span>тема юнита</span>
              <span>ключевое понятие</span>
              <span title="можно пропустить — вне гейта">⏭️</span>
              <span />
            </div>
            {res.units.map((u, i) => (
              <div key={u.key || `u-${i}`} className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1.2fr)_34px_26px] items-center gap-3 border-b border-bd px-3.5 py-1.5" data-testid="unit-row">
                <span className="font-mono text-[11px] text-mut">{i + 1}</span>
                <input
                  aria-label={`тема юнита ${i + 1}`}
                  value={u.topic}
                  placeholder="тема юнита"
                  onChange={(e) => setUnit(i, (x) => ({ ...x, topic: e.target.value }))}
                  className={`${GHOST_INPUT} font-sans text-[12.5px] ${u.skip ? "text-ink2" : ""}`}
                />
                <input
                  aria-label={`ключевое понятие ${i + 1}`}
                  value={u.concept}
                  placeholder="ключевое понятие"
                  onChange={(e) => setUnit(i, (x) => ({ ...x, concept: e.target.value }))}
                  className={`${GHOST_INPUT} font-mono text-[11px] text-ink2`}
                />
                <button
                  type="button"
                  aria-pressed={!!u.skip}
                  aria-label={`⏭️ можно пропустить · юнит ${i + 1}`}
                  title={u.skip ? "можно пропустить — не входит в гейт" : "обязательный — отметить «можно пропустить»"}
                  onClick={() => setUnit(i, toggleSkip)}
                  className={`cursor-pointer rounded-sm border px-1 py-[3px] font-mono text-[11px] leading-none ${u.skip ? "border-info bg-infosoft text-info" : "border-bd bg-transparent text-mut hover:text-ink"}`}
                  style={{ filter: u.skip ? undefined : "grayscale(1)" }}
                >
                  ⏭️
                </button>
                <IconButton onClick={() => update((r) => ({ ...r, units: r.units.filter((_, j) => j !== i) }))} aria-label={`удалить юнит ${i + 1}`}>
                  ×
                </IconButton>
              </div>
            ))}
            <div className="flex items-center gap-3 px-3.5 py-2.5">
              <DashedButton onClick={() => update((r) => ({ ...r, units: [...r.units, { key: "", topic: "", concept: "" }] }))}>+ юнит гайда</DashedButton>
              {skipped > 0 && <span className="font-mono text-[10.5px] text-mut">⏭️ юниты студент видит серыми и может пропустить — гейт их не ждёт</span>}
            </div>
          </div>
        </>
      )}
    </article>
  );
}

const FIELD_LABEL = "font-mono text-[10.5px] text-mut";

/** Поля ресурса из отзывов студентов: первоисточник, что читать, перевод, оценка, дата проверки. */
function ResourceFields({ res, update, stale }: { res: Resource; update: (fn: (r: Resource) => Resource) => void; stale: boolean }) {
  const set = (key: TextField) => (e: { target: { value: string } }) => update((r) => setField(r, key, e.target.value));
  return (
    <div className="grid grid-cols-2 gap-x-3.5 gap-y-2.5 border-t border-bd px-3.5 py-3" data-testid="resource-fields">
      <label className="col-span-2 flex w-fit cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={!!res.optional}
          onChange={(e) =>
            update((r) => {
              const next = { ...r };
              if (e.target.checked) next.optional = true;
              else delete next.optional;
              return next;
            })
          }
          className="size-[15px] cursor-pointer accent-[var(--acc)]"
          aria-label="первоисточник · вне гейта"
        />
        <span className="text-[12.5px] text-ink">Первоисточник · вне гейта</span>
        <span className="font-mono text-[10.5px] text-mut">необязательное чтение: студент видит его отдельной свёрнутой секцией, гейт его не ждёт</span>
      </label>
      <label className="col-span-2 flex flex-col gap-1">
        <span className={FIELD_LABEL}>что читать, что пропустить</span>
        <textarea
          aria-label="что читать, что пропустить"
          rows={2}
          value={res.reading_guide ?? ""}
          placeholder="2–3 строки: какие главы нужны, какие можно пропустить"
          onChange={set("reading_guide")}
          className={`${INPUT} resize-y font-sans text-[12.5px] leading-[1.5]`}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className={FIELD_LABEL}>перевод</span>
        <input aria-label="ссылка на перевод" value={res.translation_url ?? ""} placeholder="https://… — если есть" onChange={set("translation_url")} className={`${INPUT} font-mono text-[12px]`} />
      </label>
      <label className="flex flex-col gap-1">
        <span className={FIELD_LABEL}>оценка времени</span>
        <input aria-label="оценка времени" value={res.estimate ?? ""} placeholder="~4 часа · 3–5 вечеров — это нормально" onChange={set("estimate")} className={`${INPUT} font-sans text-[12.5px]`} />
      </label>
      <div className="col-span-2 flex flex-wrap items-end gap-2.5">
        <label className="flex flex-col gap-1">
          <span className={FIELD_LABEL}>проверено на актуальность</span>
          <input aria-label="дата проверки" type="date" value={res.checked_on ?? ""} onChange={set("checked_on")} className={`${INPUT} py-[6px] font-mono text-[12px]`} />
        </label>
        <button
          type="button"
          onClick={() => update((r) => ({ ...r, checked_on: todayISO() }))}
          className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-2.5 py-[7px] font-mono text-[11px] text-ink2 hover:border-acc hover:text-acc"
        >
          отметить проверенным сегодня
        </button>
        <span className="pb-[7px] font-mono text-[10.5px]" style={{ color: stale ? "var(--warn)" : "var(--mut)" }}>
          {stale ? "старше 6 месяцев — ссылки и версии могли устареть" : res.checked_on ? "студент видит «актуально на ММ.ГГГГ»" : "без даты студент не видит, насколько ресурс свежий"}
        </span>
      </div>
    </div>
  );
}
